import { CombinedWidgetContent, WidgetQuickSurveyContent, WidgetQuickSurveyContentArr } from 'jamespot-user-api';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { JRCInputCheckbox } from '../../..';

import { Column, FormContainer } from './JRCWidgetQuickSurveyEditor.styles';
import { JRCWidgetQuickSurveyEditorEntries } from './JRCWidgetQuickSurveyEditorEntries';

type JRCWidgetSurveyEditorProps = {
    token?: string;
    uniqid: string;
    content: WidgetQuickSurveyContent;
    inplace?: boolean;
    onChangeContent: (
        uniqid: string,
        content: {
            arr: WidgetQuickSurveyContentArr[];
        },
    ) => void;
    onChangeWrapper: ({ uniqid, title }: { uniqid: string; title: string }) => void;
    onSave?: (uniqid: string, content: Partial<CombinedWidgetContent>) => void;
    handleChange: (content: Partial<WidgetQuickSurveyContent>) => void;
};

export const JRCWidgetQuickSurveyEditor = ({ token, content, inplace, handleChange }: JRCWidgetSurveyEditorProps) => {
    const intl = useIntl();

    const { control, setValue, watch } = useForm({
        defaultValues: {
            multi: content.multi ?? false,
            closing: content.closing ?? false,
            seeResponseAfter: content.seeResponseAfter ?? false,
            anonymize: content.anonymize ?? false,
            open: content.open ?? false,
        },
        criteriaMode: 'all',
    });

    const seeResponseAfter = watch('seeResponseAfter');
    const multi = watch('multi');

    useEffect(() => {
        const subscription = watch((data) => {
            handleChange({ ...content, ...data });
        });
        return () => subscription.unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watch, handleChange]);

    // Effect to set multi to false when seeResponseAfter is true
    useEffect(() => {
        if (seeResponseAfter) {
            setValue('multi', false);
        }
    }, [seeResponseAfter, setValue]);

    // Effect to set seeResponseAfter to false when multi is true
    useEffect(() => {
        if (multi) {
            setValue('seeResponseAfter', false);
        }
    }, [multi, setValue]);
    return (
        <Column>
            <FormContainer>
                {token && (
                    <JRCWidgetQuickSurveyEditorEntries content={content} token={token} handleChange={handleChange} />
                )}
                {!inplace && (
                    <Column>
                        <h3>{intl.formatMessage({ id: 'GLOBAL_Options' })}</h3>
                        <Column>
                            <JRCInputCheckbox
                                name="closing"
                                checkboxMode="toggle"
                                valueTypeIsBoolean={true}
                                options={[
                                    {
                                        label: intl.formatMessage({ id: 'WIDGET_Survey_Closing' }),
                                        value: 1,
                                    },
                                ]}
                                control={control}
                                description={intl.formatMessage({ id: 'WIDGET_Survey_Closing_Help' })}
                            />
                            <JRCInputCheckbox
                                name="open"
                                checkboxMode="toggle"
                                valueTypeIsBoolean={true}
                                options={[
                                    {
                                        label: intl.formatMessage({ id: 'WIDGET_Survey_Open' }),
                                        value: 1,
                                    },
                                ]}
                                control={control}
                                description={intl.formatMessage({ id: 'WIDGET_Survey_Open_Help' })}
                            />
                            <JRCInputCheckbox
                                name="seeResponseAfter"
                                checkboxMode="toggle"
                                valueTypeIsBoolean={true}
                                options={[
                                    {
                                        label: intl.formatMessage({ id: 'POLL_See_Response_After' }),
                                        value: 1,
                                    },
                                ]}
                                control={control}
                                description={intl.formatMessage({ id: 'WIDGET_Survey_After_Help' })}
                            />
                            {!seeResponseAfter && (
                                <JRCInputCheckbox
                                    name="multi"
                                    checkboxMode="toggle"
                                    valueTypeIsBoolean={true}
                                    options={[
                                        {
                                            label: intl.formatMessage({ id: 'WIDGET_Survey_Multiple' }),
                                            value: 1,
                                        },
                                    ]}
                                    control={control}
                                    description={intl.formatMessage({ id: 'WIDGET_Survey_Multiple_Help' })}
                                />
                            )}
                            <JRCInputCheckbox
                                name="anonymize"
                                checkboxMode="toggle"
                                valueTypeIsBoolean={true}
                                options={[
                                    {
                                        label: intl.formatMessage({ id: 'SOCIALPOLL_Anonymous' }),
                                        value: 1,
                                    },
                                ]}
                                control={control}
                                description={intl.formatMessage({ id: 'WIDGET_Survey_Anonymize_Help' })}
                            />
                        </Column>
                    </Column>
                )}
            </FormContainer>
        </Column>
    );
};
